import React, { useState, useRef } from "react";
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  Chip, Button, TextField, Stack, Divider, Tooltip, MenuItem,
  Select, FormControl, InputLabel, Paper, Avatar, IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AddIcon from "@mui/icons-material/Add";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import {
  useGetCategoryDropdown, useCreatePaymentSource, useToggleSourceVisibility,
  useCreateCategory, useToggleVisibility,
  useGetTransactionTypes, useCreateTransactionType, useToggleTransactionType,
  useUploadIcon,
} from "./categoryQueries";

const typeLabel = (value) =>
  value === "CREDIT" ? "Inflow" : value === "DEBIT" ? "Outflow" : value;

const VisibilityBtn = ({ isActive, disabled, onClick }) => (
  <Tooltip title={isActive ? "Hide" : "Show"}>
    <Button
      size="small"
      variant="outlined"
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      color={isActive ? "warning" : "success"}
      startIcon={isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      sx={{ minWidth: 85 }}
    >
      {isActive ? "Hide" : "Show"}
    </Button>
  </Tooltip>
);

// ── Left panel: payment sources & sub-categories ──────────────────────────
const IconUploadBtn = ({ source }) => {
  const inputRef = useRef();
  const { mutate: uploadIcon, isLoading } = useUploadIcon();

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadIcon({ id: source.id, file });
    e.target.value = "";
  };

  return (
    <Tooltip title="Upload icon">
      <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
        <Avatar
          src={source.icon || undefined}
          variant="rounded"
          sx={{ width: 32, height: 32, bgcolor: "grey.200", cursor: "pointer", border: "1px solid", borderColor: "grey.300" }}
          onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
        >
          {!source.icon && <PhotoCameraIcon fontSize="small" sx={{ color: "grey.500" }} />}
        </Avatar>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFile}
          disabled={isLoading}
        />
      </Box>
    </Tooltip>
  );
};

const CategoriesPanel = () => {
  const { data, isLoading } = useGetCategoryDropdown();
  const { mutate: createPaymentSource, isLoading: creatingSource } = useCreatePaymentSource();
  const { mutate: toggleSource, isLoading: togglingSource } = useToggleSourceVisibility();
  const { mutate: createCategory, isLoading: creatingCat } = useCreateCategory();
  const { mutate: toggleCat, isLoading: togglingCat } = useToggleVisibility();
  const { data: txTypeData } = useGetTransactionTypes();

  // All tx types from API — show hidden ones in red but keep selectable
  const txTypeOptions = txTypeData?.data?.length
    ? txTypeData.data
    : [
        { value: "CREDIT", label: "Inflow", isActive: true },
        { value: "DEBIT", label: "Outflow", isActive: true },
      ];

  const [newSource, setNewSource] = useState({ name: "", type: "CREDIT" });

  // Sync default type selection once API data arrives
  React.useEffect(() => {
    if (txTypeOptions.length && newSource.type === "CREDIT" && !txTypeOptions.find(t => t.value === "CREDIT")) {
      setNewSource((p) => ({ ...p, type: txTypeOptions[0].value }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txTypeData]);
  const [newCatName, setNewCatName] = useState({});

  const sources = data?.data?.paymentSources || [];

  const handleAddSource = () => {
    const name = newSource.name.trim();
    if (!name) return;
    createPaymentSource(
      { name, type: newSource.type, mathematicalType: newSource.mathematicalType || newSource.type, point: 1 },
      { onSuccess: () => setNewSource({ name: "", type: "CREDIT" }) }
    );
  };

  const handleAddCat = (paymentSourceId) => {
    const name = (newCatName[paymentSourceId] || "").trim();
    if (!name) return;
    createCategory(
      { paymentSourceId, name },
      { onSuccess: () => setNewCatName((p) => ({ ...p, [paymentSourceId]: "" })) }
    );
  };

  if (isLoading) return null;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h6" fontWeight={600} mb={2} color="primary">
        Manage Categories
      </Typography>

      {/* Add new main category */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
          Add New Main Category
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Category name"
            value={newSource.name}
            onChange={(e) => setNewSource((p) => ({ ...p, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && handleAddSource()}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={newSource.type}
              onChange={(e) => setNewSource((p) => ({ ...p, type: e.target.value }))}
            >
              {txTypeOptions.map((t) => (
                <MenuItem
                  key={t.value}
                  value={t.value}
                  sx={t.isActive === false ? { color: "error.main" } : {}}
                >
                  {t.label}
                  {t.isActive === false && (
                    <span style={{ fontSize: 11, marginLeft: 6, opacity: 0.7 }}>(hidden)</span>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Math Type</InputLabel>
            <Select
              label="Math Type"
              value={newSource.mathematicalType || newSource.type}
              onChange={(e) => setNewSource((p) => ({ ...p, mathematicalType: e.target.value }))}
            >
              <MenuItem value="CREDIT">CREDIT (Inflow)</MenuItem>
              <MenuItem value="DEBIT">DEBIT (Outflow)</MenuItem>
              <MenuItem value="NONE">NONE</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained" size="small"
            disabled={creatingSource || !newSource.name.trim()}
            onClick={handleAddSource}
            startIcon={<AddIcon />}
          >
            Add
          </Button>
        </Stack>
      </Paper>

      {sources.length === 0 && (
        <Typography color="text.secondary">No categories found.</Typography>
      )}

      {sources.map((source) => (
        <Accordion
          key={source.id}
          defaultExpanded
          sx={{
            mb: 1, borderRadius: 2,
            opacity: source.isActive ? 1 : 0.65,
            border: "1px solid",
            borderColor: source.isActive ? "grey.200" : "warning.light",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, mr: 1 }}>
              <IconUploadBtn source={source} />
              <Typography fontWeight={600}>{source.name}</Typography>
              <Chip
                label={`Parent: ${typeLabel(source.type)}`}
                size="small"
                color={source.type === "CREDIT" ? "success" : "error"}
                variant="outlined"
              />
              <Chip
                label={`Math: ${typeLabel(source.mathematicalType)}`}
                size="small"
                color={source.mathematicalType === "CREDIT" ? "success" : source.mathematicalType === "DEBIT" ? "error" : "default"}
                variant="outlined"
              />
              <Chip label={`${source.categories.length} sub`} size="small" variant="outlined" />
              {!source.isActive && <Chip label="Hidden" size="small" color="warning" />}
              <Box sx={{ flex: 1 }} />
              <VisibilityBtn
                isActive={source.isActive}
                disabled={togglingSource}
                onClick={() => toggleSource(source.id)}
              />
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            {!source.isActive && (
              <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1 }}>
                This category is hidden — all its sub-categories are hidden from workers too.
              </Typography>
            )}
            <Stack spacing={1} mb={2}>
              {source.categories.length === 0 && (
                <Typography variant="body2" color="text.secondary">No sub-categories yet.</Typography>
              )}
              {source.categories.map((cat) => (
                <Stack
                  key={cat.id}
                  direction="row" alignItems="center" justifyContent="space-between"
                  sx={{
                    px: 2, py: 1, borderRadius: 1,
                    bgcolor: cat.isActive ? "grey.50" : "grey.200",
                    border: "1px solid", borderColor: "grey.200",
                    opacity: cat.isActive ? 1 : 0.6,
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>{cat.name}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={cat.isActive ? "Visible" : "Hidden"}
                      size="small"
                      color={cat.isActive ? "success" : "default"}
                    />
                    <VisibilityBtn
                      isActive={cat.isActive}
                      disabled={togglingCat}
                      onClick={() => toggleCat({ paymentSourceId: source.id, id: cat.id })}
                    />
                  </Stack>
                </Stack>
              ))}
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                placeholder="New sub-category name"
                value={newCatName[source.id] || ""}
                onChange={(e) => setNewCatName((p) => ({ ...p, [source.id]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleAddCat(source.id)}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained" size="small"
                disabled={creatingCat || !(newCatName[source.id] || "").trim()}
                onClick={() => handleAddCat(source.id)}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// ── Right panel: transaction type config ──────────────────────────────────
const TransactionTypesPanel = () => {
  const { data, isLoading } = useGetTransactionTypes();
  const { mutate: create, isLoading: creating } = useCreateTransactionType();
  const { mutate: toggle, isLoading: toggling } = useToggleTransactionType();

  const [form, setForm] = useState({ value: "", label: "" });

  const types = data?.data || [];

  const handleAdd = () => {
    const value = form.value.trim().toUpperCase();
    const label = form.label.trim();
    if (!value || !label) return;
    create({ value, label }, { onSuccess: () => setForm({ value: "", label: "" }) });
  };

  if (isLoading) return null;

  return (
    <Box sx={{ width: 300, flexShrink: 0 }}>
      <Typography variant="h6" fontWeight={600} mb={2} color="primary">
        Transaction Types
      </Typography>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Stack divider={<Divider />}>
          {types.map((t) => (
            <Stack
              key={t.id}
              direction="row" alignItems="center" justifyContent="space-between"
              sx={{ px: 2, py: 1.5, opacity: t.isActive ? 1 : 0.55 }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>{t.label}</Typography>
                <Typography variant="caption" color="text.secondary">{t.value}</Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={t.isActive ? "Visible" : "Hidden"}
                  size="small"
                  color={t.isActive ? "success" : "default"}
                />
                <VisibilityBtn
                  isActive={t.isActive}
                  disabled={toggling}
                  onClick={() => toggle(t.id)}
                />
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {/* Add new type */}
      <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
          Add New Type
        </Typography>
        <Stack spacing={1}>
          <TextField
            size="small"
            placeholder="Value (e.g. CREDIT)"
            value={form.value}
            onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))}
          />
          <TextField
            size="small"
            placeholder="Label (e.g. Inflow)"
            value={form.label}
            onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          />
          <Button
            variant="contained" size="small" fullWidth
            disabled={creating || !form.value.trim() || !form.label.trim()}
            onClick={handleAdd}
            startIcon={<AddIcon />}
          >
            Add
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

// ── Page root ─────────────────────────────────────────────────────────────
const CategoryManagement = () => (
  <Box sx={{ p: 3 }}>
    <Stack direction="row" spacing={3} alignItems="flex-start">
      <CategoriesPanel />
      <TransactionTypesPanel />
    </Stack>
  </Box>
);

export default CategoryManagement;
